import React, { useState, useCallback, useMemo } from 'react';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  ArrowDownRight,
  ArrowUpRight,
  CreditCard,
  Banknote,
  Receipt,
  RotateCcw,
  XCircle,
  Settings2,
  BookOpen,
  Search,
  ChevronLeft,
  ChevronRight,
  Loader2,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Clock,
  X,
  Copy,
  Filter,
  Activity,
} from 'lucide-react';
import {
  useFinanceOverview,
  useFinanceLedger,
  useFinancePayments,
  useFinancePayouts,
  useFinanceRefunds,
  useFinanceCancellations,
  useFinanceOperations,
} from '../hooks/useFinance';
import {
  FinanceOverview,
  FinanceLedgerEntry,
  FinanceLedgerEntryType,
  FinanceLedgerEntryStatus,
  FinancePaymentItem,
  FinancePaymentStatus,
  FinancePayoutItem,
  FinancePayoutStatus,
  FinanceRefundItem,
  FinanceCancellationItem,
  FinanceCancellationType,
  FinanceOperationItem,
  FinanceLedgerParams,
  FinancePaymentsParams,
  FinancePayoutsParams,
  FinanceRefundsParams,
  FinanceCancellationsParams,
  FinanceOperationsParams,
  FinanceDailyTrendPoint,
} from '../types/finance';
import {
  cn,
  formatDollars,
  formatDollarsCompact,
  formatDateTime,
  formatNumber,
  formatPercent,
} from '../utils/helpers';
import { Avatar, Badge } from '../components/ui';

// ============================================================================
// Constants
// ============================================================================

const PAGE_SIZE = 20;

type TabKey = 'overview' | 'ledger' | 'payments' | 'payouts' | 'refunds' | 'cancellations' | 'operations';

interface TabDef {
  key: TabKey;
  label: string;
  icon: React.ElementType;
}

const TABS: TabDef[] = [
  { key: 'overview', label: 'Overview', icon: DollarSign },
  { key: 'ledger', label: 'Ledger', icon: BookOpen },
  { key: 'payments', label: 'Payments', icon: CreditCard },
  { key: 'payouts', label: 'Payouts', icon: Banknote },
  { key: 'refunds', label: 'Refunds', icon: RotateCcw },
  { key: 'cancellations', label: 'Cancellations', icon: XCircle },
  { key: 'operations', label: 'Operations', icon: Settings2 },
];

type DatePreset = 'today' | '7d' | '30d' | '90d';

const DATE_PRESETS: { label: string; value: DatePreset }[] = [
  { label: 'Today', value: 'today' },
  { label: '7 days', value: '7d' },
  { label: '30 days', value: '30d' },
  { label: '90 days', value: '90d' },
];

// ============================================================================
// Badge variant helpers
// ============================================================================

function ledgerEntryBadge(type: FinanceLedgerEntryType): { label: string; variant: 'success' | 'error' | 'warning' | 'info' | 'default' | 'muted' } {
  const map: Record<string, { label: string; variant: 'success' | 'error' | 'warning' | 'info' | 'default' | 'muted' }> = {
    PAYMENT_RECEIVED: { label: 'Payment Received', variant: 'success' },
    PAYMENT_CAPTURED: { label: 'Payment Captured', variant: 'success' },
    PLATFORM_FEE: { label: 'Platform Fee', variant: 'info' },
    PROVIDER_EARNING: { label: 'Provider Earning', variant: 'default' },
    PAYOUT_SUBMITTED: { label: 'Payout Submitted', variant: 'warning' },
    PAYOUT_COMPLETED: { label: 'Payout Completed', variant: 'success' },
    REFUND_ISSUED: { label: 'Refund Issued', variant: 'error' },
    REFUND_RECEIVED: { label: 'Refund Received', variant: 'error' },
    PENALTY_CHARGE: { label: 'Penalty', variant: 'error' },
    ADJUSTMENT: { label: 'Adjustment', variant: 'muted' },
    CREDIT_ADDED: { label: 'Credit Added', variant: 'info' },
    CREDIT_USED: { label: 'Credit Used', variant: 'info' },
    GIFT_CARD_REDEEMED: { label: 'Gift Card', variant: 'default' },
    GIFT_CARD_USED: { label: 'Gift Card Used', variant: 'default' },
    XUBUX_PURCHASE: { label: 'Xubux', variant: 'default' },
  };
  return map[type] || { label: type, variant: 'muted' };
}

function ledgerStatusBadge(status: FinanceLedgerEntryStatus): { variant: 'success' | 'warning' | 'error' | 'muted' } {
  const map: Record<string, 'success' | 'warning' | 'error' | 'muted'> = {
    RECONCILED: 'success',
    PENDING: 'warning',
    FAILED: 'error',
    REVERSED: 'muted',
  };
  return { variant: map[status] || 'muted' };
}

function paymentStatusBadge(status: FinancePaymentStatus): { variant: 'success' | 'warning' | 'error' | 'info' | 'muted' } {
  const map: Record<string, 'success' | 'warning' | 'error' | 'info' | 'muted'> = {
    COMPLETED: 'success',
    PENDING: 'warning',
    PROCESSING: 'info',
    FAILED: 'error',
    REFUNDED: 'muted',
    PARTIALLY_REFUNDED: 'muted',
  };
  return { variant: map[status] || 'muted' };
}

function payoutStatusBadge(status: FinancePayoutStatus): { variant: 'success' | 'warning' | 'error' | 'info' | 'muted' } {
  const map: Record<string, 'success' | 'warning' | 'error' | 'info' | 'muted'> = {
    PAID: 'success',
    PENDING: 'warning',
    PENDING_BALANCE: 'warning',
    SUBMITTED: 'info',
    IN_TRANSIT: 'info',
    FAILED: 'error',
    CANCELLED: 'muted',
  };
  return { variant: map[status] || 'muted' };
}

function cancellationTypeBadge(type: FinanceCancellationType): { variant: 'error' | 'warning' | 'info' | 'muted' } {
  const map: Record<string, 'error' | 'warning' | 'info' | 'muted'> = {
    CUSTOMER: 'warning',
    PROVIDER: 'error',
    SYSTEM: 'info',
    AUTO_EXPIRED: 'muted',
    CONFLICT: 'error',
  };
  return { variant: map[type] || 'muted' };
}

function operationStatusBadge(status: string): { variant: 'success' | 'warning' | 'error' | 'info' | 'muted' } {
  const map: Record<string, 'success' | 'warning' | 'error' | 'info' | 'muted'> = {
    COMPLETED: 'success',
    PENDING: 'warning',
    PROCESSING: 'info',
    FAILED: 'error',
    RETRYING: 'warning',
  };
  return { variant: map[status] || 'muted' };
}

// ============================================================================
// StatCard
// ============================================================================

interface StatCardProps {
  label: string;
  value: string | number;
  icon: React.ElementType;
  color: string;
  bgColor: string;
  loading?: boolean;
  subtitle?: string;
}

const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  icon: Icon,
  color,
  bgColor,
  loading,
  subtitle,
}) => (
  <div className="card p-4">
    <div className="flex items-center gap-3">
      <div className={cn('p-2 rounded-xl flex-shrink-0', bgColor)}>
        <Icon className={cn('h-5 w-5', color)} />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground truncate">{label}</p>
        {loading ? (
          <div className="h-7 w-16 bg-muted/30 rounded animate-pulse mt-0.5" />
        ) : (
          <p className="text-xl font-bold text-foreground">{value}</p>
        )}
        {subtitle && !loading && (
          <p className="text-[10px] text-muted-foreground">{subtitle}</p>
        )}
      </div>
    </div>
  </div>
);

// ============================================================================
// DailyTrendChart - SVG area chart with multi-series
// ============================================================================

interface DailyTrendChartProps {
  data: FinanceDailyTrendPoint[];
  loading?: boolean;
  height?: number;
}

const DailyTrendChart: React.FC<DailyTrendChartProps> = ({ data, loading, height = 200 }) => {
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  if (loading) {
    return (
      <div className="flex items-end gap-0.5" style={{ height }}>
        {Array.from({ length: 14 }).map((_, i) => (
          <div
            key={i}
            className="flex-1 bg-muted/20 rounded-t animate-pulse"
            style={{ height: `${20 + Math.random() * 60}%` }}
          />
        ))}
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center text-xs text-muted-foreground" style={{ height }}>
        No trend data available
      </div>
    );
  }

  const padding = 8;
  const chartWidth = 500;
  const labelHeight = 24;
  const chartHeight = height - labelHeight;

  const allValues = data.flatMap((d) => [d.revenue, d.refunds, d.net]);
  const max = Math.max(...allValues, 1);
  const min = Math.min(...allValues, 0);
  const range = max - min || 1;

  const toPoint = (val: number, i: number) => ({
    x: padding + (i / (data.length - 1 || 1)) * (chartWidth - padding * 2),
    y: chartHeight - padding - ((val - min) / range) * (chartHeight - padding * 2),
  });

  const buildPath = (key: 'revenue' | 'refunds' | 'net') =>
    data.map((d, i) => toPoint(d[key], i))
      .map((p, i) => (i === 0 ? `M ${p.x} ${p.y}` : `L ${p.x} ${p.y}`))
      .join(' ');

  const buildArea = (key: 'revenue' | 'refunds' | 'net') => {
    const pts = data.map((d, i) => toPoint(d[key], i));
    const baseline = chartHeight - padding;
    return (
      pts.map((p, i) => (i === 0 ? `M ${p.x} ${p.y}` : `L ${p.x} ${p.y}`)).join(' ') +
      ` L ${pts[pts.length - 1].x} ${baseline} L ${pts[0].x} ${baseline} Z`
    );
  };

  const labelIndices: number[] = [];
  const labelCount = Math.min(6, data.length);
  for (let i = 0; i < labelCount; i++) {
    labelIndices.push(Math.round((i / (labelCount - 1 || 1)) * (data.length - 1)));
  }

  return (
    <div style={{ height }} className="relative">
      <svg
        viewBox={`0 0 ${chartWidth} ${height}`}
        className="w-full h-full"
        preserveAspectRatio="none"
      >
        <defs>
          <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#10b981" stopOpacity="0.2" />
            <stop offset="100%" stopColor="#10b981" stopOpacity="0.02" />
          </linearGradient>
          <linearGradient id="refGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#ef4444" stopOpacity="0.15" />
            <stop offset="100%" stopColor="#ef4444" stopOpacity="0.02" />
          </linearGradient>
        </defs>

        {/* Grid */}
        {[0, 0.25, 0.5, 0.75, 1].map((frac) => {
          const y = chartHeight - padding - frac * (chartHeight - padding * 2);
          return (
            <line
              key={frac}
              x1={padding}
              y1={y}
              x2={chartWidth - padding}
              y2={y}
              stroke="currentColor"
              className="text-border"
              strokeWidth="0.5"
              strokeDasharray="4 4"
              opacity={0.4}
            />
          );
        })}

        {/* Area fills */}
        <path d={buildArea('revenue')} fill="url(#revGrad)" />
        <path d={buildArea('refunds')} fill="url(#refGrad)" />

        {/* Lines */}
        <path d={buildPath('revenue')} fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d={buildPath('refunds')} fill="none" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="4 2" />
        <path d={buildPath('net')} fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />

        {/* Hover targets */}
        {data.map((_, i) => {
          const x = padding + (i / (data.length - 1 || 1)) * (chartWidth - padding * 2);
          return (
            <rect
              key={i}
              x={x - (chartWidth / data.length) / 2}
              y={0}
              width={chartWidth / data.length}
              height={chartHeight}
              fill="transparent"
              onMouseEnter={() => setHoverIdx(i)}
              onMouseLeave={() => setHoverIdx(null)}
            />
          );
        })}

        {/* Hover line */}
        {hoverIdx !== null && (
          <line
            x1={toPoint(0, hoverIdx).x}
            y1={padding}
            x2={toPoint(0, hoverIdx).x}
            y2={chartHeight - padding}
            stroke="currentColor"
            className="text-muted-foreground"
            strokeWidth="1"
            strokeDasharray="3 3"
            opacity={0.6}
          />
        )}

        {/* Hover dots */}
        {hoverIdx !== null && (['revenue', 'refunds', 'net'] as const).map((key) => {
          const p = toPoint(data[hoverIdx][key], hoverIdx);
          const colors = { revenue: '#10b981', refunds: '#ef4444', net: '#3b82f6' };
          return <circle key={key} cx={p.x} cy={p.y} r="4" fill={colors[key]} stroke="#0f172a" strokeWidth="1.5" />;
        })}

        {/* X labels */}
        {labelIndices.map((idx) => {
          const pt = toPoint(0, idx);
          return (
            <text
              key={idx}
              x={pt.x}
              y={height - 4}
              textAnchor="middle"
              className="fill-muted-foreground"
              fontSize="9"
            >
              {new Date(data[idx].date + 'T00:00:00').toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
              })}
            </text>
          );
        })}
      </svg>

      {/* Hover tooltip */}
      {hoverIdx !== null && (
        <div
          className="absolute top-0 bg-card border border-border rounded-lg p-2 shadow-lg text-xs pointer-events-none z-10"
          style={{
            left: `${((padding + (hoverIdx / (data.length - 1 || 1)) * (chartWidth - padding * 2)) / chartWidth) * 100}%`,
            transform: 'translateX(-50%)',
          }}
        >
          <p className="font-medium text-foreground mb-1">
            {new Date(data[hoverIdx].date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </p>
          <p className="text-emerald-400">Revenue: {formatDollars(data[hoverIdx].revenue)}</p>
          <p className="text-red-400">Refunds: {formatDollars(data[hoverIdx].refunds)}</p>
          <p className="text-blue-400">Net: {formatDollars(data[hoverIdx].net)}</p>
        </div>
      )}

      {/* Legend */}
      <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
        <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-emerald-500 rounded inline-block" /> Revenue</span>
        <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-red-500 rounded inline-block border-dashed" /> Refunds</span>
        <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-blue-500 rounded inline-block" /> Net</span>
      </div>
    </div>
  );
};

// ============================================================================
// FilterBar
// ============================================================================

interface FilterOption {
  label: string;
  value: string;
}

interface FilterField {
  key: string;
  label: string;
  options: FilterOption[];
}

interface FilterBarProps {
  filters: FilterField[];
  values: Record<string, string>;
  onChange: (key: string, value: string) => void;
  onSearch?: (value: string) => void;
  searchValue?: string;
  searchPlaceholder?: string;
}

const FilterBar: React.FC<FilterBarProps> = ({
  filters,
  values,
  onChange,
  onSearch,
  searchValue,
  searchPlaceholder = 'Search...',
}) => (
  <div className="flex flex-wrap items-center gap-2 mb-4">
    {onSearch && (
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
        <input
          type="text"
          value={searchValue || ''}
          onChange={(e) => onSearch(e.target.value)}
          placeholder={searchPlaceholder}
          className="pl-8 pr-3 py-1.5 text-sm bg-card border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50 w-48"
        />
        {searchValue && (
          <button
            onClick={() => onSearch('')}
            className="absolute right-2 top-1/2 -translate-y-1/2"
          >
            <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
          </button>
        )}
      </div>
    )}
    {filters.map((f) => (
      <select
        key={f.key}
        value={values[f.key] || ''}
        onChange={(e) => onChange(f.key, e.target.value)}
        className="px-3 py-1.5 text-sm bg-card border border-border rounded-lg text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
      >
        <option value="">{f.label}</option>
        {f.options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    ))}
  </div>
);

// ============================================================================
// Pagination
// ============================================================================

interface PaginationProps {
  page: number;
  totalPages: number;
  total: number;
  onPageChange: (page: number) => void;
  loading?: boolean;
}

const Pagination: React.FC<PaginationProps> = ({
  page,
  totalPages,
  total,
  onPageChange,
  loading,
}) => {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
      <p className="text-xs text-muted-foreground">
        Page {page} of {totalPages} ({formatNumber(total)} total)
      </p>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1 || loading}
          className="p-1.5 rounded-lg hover:bg-primary/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronLeft className="h-4 w-4 text-foreground" />
        </button>
        {/* Page numbers */}
        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
          let pageNum: number;
          if (totalPages <= 5) {
            pageNum = i + 1;
          } else if (page <= 3) {
            pageNum = i + 1;
          } else if (page >= totalPages - 2) {
            pageNum = totalPages - 4 + i;
          } else {
            pageNum = page - 2 + i;
          }
          return (
            <button
              key={pageNum}
              onClick={() => onPageChange(pageNum)}
              disabled={loading}
              className={cn(
                'w-8 h-8 text-xs rounded-lg transition-colors',
                pageNum === page
                  ? 'bg-primary/20 text-primary font-semibold'
                  : 'text-muted-foreground hover:bg-primary/10'
              )}
            >
              {pageNum}
            </button>
          );
        })}
        <button
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages || loading}
          className="p-1.5 rounded-lg hover:bg-primary/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronRight className="h-4 w-4 text-foreground" />
        </button>
      </div>
    </div>
  );
};

// ============================================================================
// Stripe ID display helper
// ============================================================================

const StripeId: React.FC<{ id: string | null; label?: string; full?: boolean }> = ({ id, label, full }) => {
  if (!id) return null;
  const display = full ? id : (id.length > 20 ? id.slice(0, 8) + '...' + id.slice(-6) : id);
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] font-mono text-muted-foreground bg-muted/20 px-1.5 py-0.5 rounded cursor-pointer hover:text-foreground transition-colors"
      title={id}
      onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(id); }}
    >
      {label && <span className="text-muted-foreground/60">{label}:</span>}
      {display}
      <Copy className="h-2.5 w-2.5" />
    </span>
  );
};

// ============================================================================
// Detail sub-components for expanded cards
// ============================================================================

const DetailSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="pt-2 mt-2 first:mt-0 first:pt-0">
    <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">{title}</p>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
      {children}
    </div>
  </div>
);

const DetailRow: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => {
  if (value == null || value === '') return null;
  return (
    <div className="flex items-baseline justify-between gap-2 py-0.5">
      <span className="text-[10px] text-muted-foreground whitespace-nowrap">{label}</span>
      <span className="text-[10px] text-foreground text-right break-all">{value}</span>
    </div>
  );
};

// ============================================================================
// Stacked bar component
// ============================================================================

interface StackedBarEntry {
  label: string;
  value: number;
  color: string;
}

const StackedBar: React.FC<{ entries: StackedBarEntry[]; loading?: boolean }> = ({ entries, loading }) => {
  if (loading) {
    return <div className="h-6 bg-muted/20 rounded animate-pulse" />;
  }
  const total = entries.reduce((s, e) => s + e.value, 0);
  if (total === 0) {
    return <div className="h-6 bg-muted/10 rounded flex items-center justify-center text-xs text-muted-foreground">No data</div>;
  }
  return (
    <div>
      <div className="flex h-6 rounded-lg overflow-hidden">
        {entries.filter((e) => e.value > 0).map((e, i) => (
          <div
            key={i}
            className={cn('h-full transition-all', e.color)}
            style={{ width: `${(e.value / total) * 100}%` }}
            title={`${e.label}: ${formatNumber(e.value)}`}
          />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
        {entries.filter((e) => e.value > 0).map((e, i) => (
          <span key={i} className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span className={cn('w-2 h-2 rounded-full', e.color)} />
            {e.label}: {formatNumber(e.value)}
          </span>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// Donut display for reconciliation
// ============================================================================

const ReconciliationDonut: React.FC<{
  reconciled: number;
  pending: number;
  failed: number;
  loading?: boolean;
}> = ({ reconciled, pending, failed, loading }) => {
  if (loading) {
    return <div className="h-24 w-24 bg-muted/20 rounded-full animate-pulse mx-auto" />;
  }
  const total = reconciled + pending + failed;
  if (total === 0) {
    return <p className="text-xs text-muted-foreground text-center">No data</p>;
  }
  const reconPct = (reconciled / total) * 100;
  const pendPct = (pending / total) * 100;
  const failPct = (failed / total) * 100;

  const r = 40;
  const circ = 2 * Math.PI * r;

  const reconDash = (reconPct / 100) * circ;
  const pendDash = (pendPct / 100) * circ;
  const failDash = (failPct / 100) * circ;

  return (
    <div className="flex flex-col items-center gap-2">
      <svg width="100" height="100" viewBox="0 0 100 100">
        <circle cx="50" cy="50" r={r} fill="none" stroke="currentColor" className="text-muted/20" strokeWidth="8" />
        {/* Reconciled */}
        <circle
          cx="50" cy="50" r={r}
          fill="none" stroke="#10b981" strokeWidth="8"
          strokeDasharray={`${reconDash} ${circ - reconDash}`}
          strokeDashoffset={circ * 0.25}
          strokeLinecap="round"
        />
        {/* Pending */}
        <circle
          cx="50" cy="50" r={r}
          fill="none" stroke="#eab308" strokeWidth="8"
          strokeDasharray={`${pendDash} ${circ - pendDash}`}
          strokeDashoffset={circ * 0.25 - reconDash}
          strokeLinecap="round"
        />
        {/* Failed */}
        <circle
          cx="50" cy="50" r={r}
          fill="none" stroke="#ef4444" strokeWidth="8"
          strokeDasharray={`${failDash} ${circ - failDash}`}
          strokeDashoffset={circ * 0.25 - reconDash - pendDash}
          strokeLinecap="round"
        />
        <text x="50" y="50" textAnchor="middle" dominantBaseline="middle" className="fill-foreground text-lg font-bold" fontSize="16">
          {reconPct.toFixed(0)}%
        </text>
      </svg>
      <div className="flex flex-wrap justify-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" />Reconciled: {formatNumber(reconciled)}</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-500" />Pending: {formatNumber(pending)}</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" />Failed: {formatNumber(failed)}</span>
      </div>
    </div>
  );
};

// ============================================================================
// Overview Tab
// ============================================================================

const OverviewTab: React.FC = () => {
  const [preset, setPreset] = useState<DatePreset>('30d');
  const { data, loading, refetch } = useFinanceOverview({ preset });

  const ov = data;

  return (
    <div className="space-y-6">
      {/* Date preset + refresh */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1 bg-card border border-border rounded-lg p-1">
          {DATE_PRESETS.map((p) => (
            <button
              key={p.value}
              onClick={() => setPreset(p.value)}
              className={cn(
                'px-3 py-1.5 text-xs rounded-md transition-colors',
                preset === p.value
                  ? 'bg-primary/20 text-primary font-medium'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {p.label}
            </button>
          ))}
        </div>
        <button
          onClick={() => refetch()}
          disabled={loading}
          className="p-2 rounded-lg hover:bg-primary/10 transition-colors"
        >
          <RefreshCw className={cn('h-4 w-4 text-muted-foreground', loading && 'animate-spin')} />
        </button>
      </div>

      {/* Platform Balance (all-time) */}
      <div>
        <h3 className="text-sm font-semibold text-foreground mb-3">Platform Balance</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard
            label="Total Revenue"
            value={formatDollarsCompact(ov?.platformBalance.totalRevenueCollected)}
            icon={TrendingUp}
            color="text-emerald-400"
            bgColor="bg-emerald-400/10"
            loading={loading}
          />
          <StatCard
            label="Total Refunded"
            value={formatDollarsCompact(ov?.platformBalance.totalRefunded)}
            icon={RotateCcw}
            color="text-red-400"
            bgColor="bg-red-400/10"
            loading={loading}
          />
          <StatCard
            label="Total Payouts"
            value={formatDollarsCompact(ov?.platformBalance.totalPayouts)}
            icon={ArrowUpRight}
            color="text-blue-400"
            bgColor="bg-blue-400/10"
            loading={loading}
          />
          <StatCard
            label="Net Balance"
            value={formatDollarsCompact(ov?.platformBalance.netPlatformBalance)}
            icon={DollarSign}
            color="text-purple-400"
            bgColor="bg-purple-400/10"
            loading={loading}
          />
        </div>
      </div>

      {/* Period Metrics */}
      <div>
        <h3 className="text-sm font-semibold text-foreground mb-3">Period Metrics</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard
            label="Payments"
            value={formatDollarsCompact(ov?.periodMetrics.payments.sum)}
            icon={CreditCard}
            color="text-emerald-400"
            bgColor="bg-emerald-400/10"
            loading={loading}
            subtitle={ov ? `${ov.periodMetrics.payments.count} txns` : undefined}
          />
          <StatCard
            label="Refunds"
            value={formatDollarsCompact(ov?.periodMetrics.refunds.sum)}
            icon={RotateCcw}
            color="text-red-400"
            bgColor="bg-red-400/10"
            loading={loading}
            subtitle={ov ? `${ov.periodMetrics.refunds.count} txns` : undefined}
          />
          <StatCard
            label="Platform Fees"
            value={formatDollarsCompact(ov?.periodMetrics.platformFees.sum)}
            icon={Receipt}
            color="text-cyan-400"
            bgColor="bg-cyan-400/10"
            loading={loading}
            subtitle={ov ? `${ov.periodMetrics.platformFees.count} txns` : undefined}
          />
          <StatCard
            label="Provider Payouts"
            value={formatDollarsCompact(ov?.periodMetrics.providerPayouts.sum)}
            icon={Banknote}
            color="text-blue-400"
            bgColor="bg-blue-400/10"
            loading={loading}
            subtitle={ov ? `${ov.periodMetrics.providerPayouts.count} txns` : undefined}
          />
          <StatCard
            label="Penalties"
            value={formatDollarsCompact(ov?.periodMetrics.penalties.sum)}
            icon={AlertTriangle}
            color="text-orange-400"
            bgColor="bg-orange-400/10"
            loading={loading}
            subtitle={ov ? `${ov.periodMetrics.penalties.count} txns` : undefined}
          />
          <StatCard
            label="Credits Redeemed"
            value={formatDollarsCompact(ov?.periodMetrics.creditsRedeemed)}
            icon={ArrowDownRight}
            color="text-indigo-400"
            bgColor="bg-indigo-400/10"
            loading={loading}
          />
          <StatCard
            label="Gift Cards Redeemed"
            value={formatDollarsCompact(ov?.periodMetrics.giftCardsRedeemed)}
            icon={CreditCard}
            color="text-pink-400"
            bgColor="bg-pink-400/10"
            loading={loading}
          />
          <StatCard
            label="Net Revenue"
            value={formatDollarsCompact(ov?.periodMetrics.netRevenue)}
            icon={DollarSign}
            color="text-emerald-400"
            bgColor="bg-emerald-400/10"
            loading={loading}
          />
        </div>
      </div>

      {/* Daily Trend Chart */}
      <div className="card p-4">
        <h3 className="text-sm font-semibold text-foreground mb-3">Daily Trend</h3>
        <DailyTrendChart data={ov?.dailyTrend || []} loading={loading} height={220} />
      </div>

      {/* Bottom row: Failure Rate, Cancellation Summary, Payout Status, Reconciliation */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Failure Rate */}
        <div className="card p-4">
          <h4 className="text-xs font-semibold text-muted-foreground mb-2">Payment Failure Rate</h4>
          {loading ? (
            <div className="h-12 bg-muted/20 rounded animate-pulse" />
          ) : (
            <div>
              <p className="text-3xl font-bold text-foreground">{formatPercent(ov?.paymentFailureRate.rate)}</p>
              <p className="text-[10px] text-muted-foreground mt-1">
                {ov?.paymentFailureRate.failedCount || 0} failed / {ov?.paymentFailureRate.totalAttempts || 0} attempts
              </p>
              <div className="mt-2 h-2 bg-muted/20 rounded-full overflow-hidden">
                <div
                  className={cn(
                    'h-full rounded-full transition-all',
                    (ov?.paymentFailureRate.rate || 0) > 0.05 ? 'bg-red-500' : (ov?.paymentFailureRate.rate || 0) > 0.02 ? 'bg-yellow-500' : 'bg-emerald-500'
                  )}
                  style={{ width: `${Math.min((ov?.paymentFailureRate.rate || 0) * 100, 100)}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Cancellation Summary */}
        <div className="card p-4">
          <h4 className="text-xs font-semibold text-muted-foreground mb-2">
            Cancellations ({formatNumber(ov?.cancellationSummary?.reduce((s, c) => s + c.count, 0))})
          </h4>
          <StackedBar
            loading={loading}
            entries={(ov?.cancellationSummary || []).map((c) => {
              const colorMap: Record<string, string> = {
                CUSTOMER: 'bg-yellow-500',
                PROVIDER: 'bg-red-500',
                SYSTEM: 'bg-blue-500',
                AUTO_EXPIRED: 'bg-gray-500',
                CONFLICT: 'bg-orange-500',
              };
              return { label: c.type, value: c.count, color: colorMap[c.type] || 'bg-gray-400' };
            })}
          />
        </div>

        {/* Payout Status Breakdown */}
        <div className="card p-4">
          <h4 className="text-xs font-semibold text-muted-foreground mb-2">Payout Status</h4>
          <StackedBar
            loading={loading}
            entries={(ov?.payoutStatusBreakdown || []).map((p) => {
              const colorMap: Record<string, string> = {
                PAID: 'bg-emerald-500',
                IN_TRANSIT: 'bg-blue-500',
                SUBMITTED: 'bg-cyan-500',
                PENDING: 'bg-yellow-500',
                PENDING_BALANCE: 'bg-amber-500',
                FAILED: 'bg-red-500',
                CANCELLED: 'bg-gray-500',
              };
              return { label: p.status.replace(/_/g, ' '), value: p.count, color: colorMap[p.status] || 'bg-gray-400' };
            })}
          />
        </div>

        {/* Reconciliation */}
        <div className="card p-4">
          <h4 className="text-xs font-semibold text-muted-foreground mb-2">Reconciliation</h4>
          <ReconciliationDonut
            reconciled={ov?.reconciliationStatus.reconciled || 0}
            pending={ov?.reconciliationStatus.pending || 0}
            failed={ov?.reconciliationStatus.failed || 0}
            loading={loading}
          />
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Ledger Tab
// ============================================================================

const LedgerTab: React.FC = () => {
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const params = useMemo<FinanceLedgerParams>(() => ({
    entryType: filters.entryType || undefined,
    status: filters.status || undefined,
    account: filters.account || undefined,
    search: search || undefined,
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  }), [filters, search, page]);

  const { items, total, totalPages, loading, refetch, page: currentPage } = useFinanceLedger(params);

  const handleFilterChange = useCallback((key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  }, []);

  const handleSearch = useCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, []);

  const filterFields: FilterField[] = [
    {
      key: 'entryType',
      label: 'All Types',
      options: [
        { label: 'Payment Received', value: 'PAYMENT_RECEIVED' },
        { label: 'Payment Captured', value: 'PAYMENT_CAPTURED' },
        { label: 'Platform Fee', value: 'PLATFORM_FEE' },
        { label: 'Provider Earning', value: 'PROVIDER_EARNING' },
        { label: 'Payout Submitted', value: 'PAYOUT_SUBMITTED' },
        { label: 'Payout Completed', value: 'PAYOUT_COMPLETED' },
        { label: 'Refund Issued', value: 'REFUND_ISSUED' },
        { label: 'Penalty', value: 'PENALTY_CHARGE' },
        { label: 'Adjustment', value: 'ADJUSTMENT' },
        { label: 'Credit Added', value: 'CREDIT_ADDED' },
        { label: 'Credit Used', value: 'CREDIT_USED' },
        { label: 'Gift Card', value: 'GIFT_CARD_REDEEMED' },
      ],
    },
    {
      key: 'status',
      label: 'All Statuses',
      options: [
        { label: 'Pending', value: 'PENDING' },
        { label: 'Reconciled', value: 'RECONCILED' },
        { label: 'Failed', value: 'FAILED' },
        { label: 'Reversed', value: 'REVERSED' },
      ],
    },
  ];

  return (
    <div>
      <FilterBar
        filters={filterFields}
        values={filters}
        onChange={handleFilterChange}
        onSearch={handleSearch}
        searchValue={search}
        searchPlaceholder="Search ledger..."
      />

      {loading && items.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 text-primary animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground text-sm">No ledger entries found</div>
      ) : (
        <div className="space-y-2">
          {items.map((entry) => {
            const typeBadge = ledgerEntryBadge(entry.entryType);
            const statusBadge = ledgerStatusBadge(entry.status);
            const isPositive = entry.amount >= 0;

            const isExpanded = expandedId === entry.id;
            return (
              <div
                key={entry.id}
                className={cn(
                  'card p-3 cursor-pointer transition-all',
                  isExpanded && 'ring-1 ring-primary/30'
                )}
                onClick={() => setExpandedId(isExpanded ? null : entry.id)}
              >
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="flex-shrink-0 w-16 text-xs text-muted-foreground">
                      {new Date(entry.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </div>
                    <Badge variant={typeBadge.variant} size="sm">{typeBadge.label}</Badge>
                    <span className="text-xs text-muted-foreground truncate">{entry.account}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={cn('text-sm font-semibold tabular-nums', isPositive ? 'text-emerald-400' : 'text-red-400')}>
                      {isPositive ? '+' : ''}{formatDollars(entry.amount)}
                    </span>
                    <Badge variant={statusBadge.variant} size="sm">{entry.status}</Badge>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {entry.user && (
                      <span className="text-xs text-muted-foreground">{entry.user.name || entry.user.email}</span>
                    )}
                    <span className="text-xs text-muted-foreground truncate max-w-[200px]">{entry.description}</span>
                    <StripeId id={entry.stripePaymentIntentId} label="PI" />
                    <StripeId id={entry.stripeChargeId} label="CH" />
                  </div>
                </div>
                {isExpanded && (
                  <div className="border-t border-border mt-3 pt-3 space-y-3" onClick={(e) => e.stopPropagation()}>
                    <DetailSection title="Transaction">
                      <DetailRow label="Transaction ID" value={entry.transactionId} />
                      <DetailRow label="Currency" value={entry.currency} />
                      <DetailRow label="Description" value={entry.description} />
                    </DetailSection>
                    <DetailSection title="Relations">
                      {entry.user && (
                        <DetailRow label="User" value={`${entry.user.name || '—'} (${entry.user.email || '—'})`} />
                      )}
                      {entry.booking && (
                        <DetailRow label="Booking" value={`#${entry.booking.id} · ${entry.booking.status}`} />
                      )}
                      {entry.payment && (
                        <>
                          <DetailRow label="Payment" value={`#${entry.payment.id} · ${entry.payment.status}`} />
                          {entry.payment.stripePaymentId && (
                            <DetailRow label="Payment Stripe" value={entry.payment.stripePaymentId} />
                          )}
                        </>
                      )}
                      {entry.payout && (
                        <>
                          <DetailRow label="Payout" value={`#${entry.payout.id} · ${entry.payout.status}`} />
                          {entry.payout.stripePayoutId && (
                            <DetailRow label="Payout Stripe" value={entry.payout.stripePayoutId} />
                          )}
                        </>
                      )}
                    </DetailSection>
                    <DetailSection title="Stripe IDs">
                      <div className="col-span-full flex flex-wrap gap-1.5">
                        <StripeId id={entry.stripePaymentIntentId} label="PI" full />
                        <StripeId id={entry.stripeChargeId} label="CH" full />
                        <StripeId id={entry.stripeTransferId} label="TR" full />
                        <StripeId id={entry.stripePayoutId} label="PO" full />
                        <StripeId id={entry.stripeRefundId} label="RE" full />
                      </div>
                    </DetailSection>
                    <DetailSection title="Timeline">
                      <DetailRow label="Created" value={formatDateTime(entry.createdAt)} />
                      <DetailRow label="Updated" value={formatDateTime(entry.updatedAt)} />
                      {entry.reconciledAt && <DetailRow label="Reconciled" value={formatDateTime(entry.reconciledAt)} />}
                      {entry.reconciledBy && <DetailRow label="Reconciled By" value={entry.reconciledBy} />}
                    </DetailSection>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Pagination
        page={currentPage}
        totalPages={totalPages}
        total={total}
        onPageChange={setPage}
        loading={loading}
      />
    </div>
  );
};

// ============================================================================
// Payments Tab
// ============================================================================

const PaymentsTab: React.FC = () => {
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const params = useMemo<FinancePaymentsParams>(() => ({
    status: filters.status || undefined,
    paymentMethod: filters.paymentMethod || undefined,
    cardBrand: filters.cardBrand || undefined,
    search: search || undefined,
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  }), [filters, search, page]);

  const { items, total, totalPages, loading, page: currentPage } = useFinancePayments(params);

  const handleFilterChange = useCallback((key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  }, []);

  const filterFields: FilterField[] = [
    {
      key: 'status',
      label: 'All Statuses',
      options: [
        { label: 'Completed', value: 'COMPLETED' },
        { label: 'Pending', value: 'PENDING' },
        { label: 'Processing', value: 'PROCESSING' },
        { label: 'Failed', value: 'FAILED' },
        { label: 'Refunded', value: 'REFUNDED' },
        { label: 'Partially Refunded', value: 'PARTIALLY_REFUNDED' },
      ],
    },
    {
      key: 'paymentMethod',
      label: 'All Methods',
      options: [
        { label: 'Credit Card', value: 'CREDIT_CARD' },
        { label: 'Debit Card', value: 'DEBIT_CARD' },
        { label: 'Bank Transfer', value: 'BANK_TRANSFER' },
        { label: 'Free', value: 'FREE' },
      ],
    },
    {
      key: 'cardBrand',
      label: 'All Brands',
      options: [
        { label: 'Visa', value: 'visa' },
        { label: 'Mastercard', value: 'mastercard' },
        { label: 'Amex', value: 'amex' },
        { label: 'Discover', value: 'discover' },
      ],
    },
  ];

  return (
    <div>
      <FilterBar
        filters={filterFields}
        values={filters}
        onChange={handleFilterChange}
        onSearch={(v) => { setSearch(v); setPage(1); }}
        searchValue={search}
        searchPlaceholder="Search payments..."
      />

      {loading && items.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 text-primary animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground text-sm">No payments found</div>
      ) : (
        <div className="space-y-2">
          {items.map((pmt) => {
            const stBadge = paymentStatusBadge(pmt.status);
            const isExpanded = expandedId === pmt.id;
            return (
              <div
                key={pmt.id}
                className={cn(
                  'card p-3 cursor-pointer transition-all',
                  isExpanded && 'ring-1 ring-primary/30'
                )}
                onClick={() => setExpandedId(isExpanded ? null : pmt.id)}
              >
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="flex-shrink-0 w-16 text-xs text-muted-foreground">
                      {new Date(pmt.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </div>
                    <span className="text-sm font-semibold text-foreground tabular-nums">{formatDollars(pmt.amount)}</span>
                    <Badge variant={stBadge.variant} size="sm">{pmt.status}</Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    {pmt.paymentMethod && (
                      <span className="text-xs text-muted-foreground">
                        {pmt.paymentMethod.replace(/_/g, ' ')}
                        {pmt.cardBrand && pmt.cardLast4 && ` (${pmt.cardBrand} ...${pmt.cardLast4})`}
                      </span>
                    )}
                    {pmt.booking?.customer && (
                      <span className="text-xs text-muted-foreground">{pmt.booking.customer.name || pmt.booking.customer.email}</span>
                    )}
                    {pmt.booking && (
                      <span className="text-xs text-primary">Booking #{pmt.booking.id}</span>
                    )}
                  </div>
                </div>
                {/* Fee breakdown */}
                <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                  {pmt.processingFee != null && (
                    <span className="text-[10px] text-muted-foreground">Fee: {formatDollars(pmt.processingFee)}</span>
                  )}
                  {pmt.platformFee != null && (
                    <span className="text-[10px] text-muted-foreground">Platform: {formatDollars(pmt.platformFee)}</span>
                  )}
                  {pmt.providerPayout != null && (
                    <span className="text-[10px] text-muted-foreground">Provider: {formatDollars(pmt.providerPayout)}</span>
                  )}
                  {pmt.refundAmount != null && pmt.refundAmount > 0 && (
                    <Badge variant="error" size="sm">Refunded: {formatDollars(pmt.refundAmount)}</Badge>
                  )}
                  {pmt.failureReason && (
                    <span className="text-[10px] text-red-400">{pmt.failureReason}</span>
                  )}
                </div>
                {isExpanded && (
                  <div className="border-t border-border mt-3 pt-3 space-y-3" onClick={(e) => e.stopPropagation()}>
                    <DetailSection title="Amount Breakdown">
                      <DetailRow label="Amount" value={formatDollars(pmt.amount)} />
                      <DetailRow label="Processing Fee" value={pmt.processingFee != null ? formatDollars(pmt.processingFee) : undefined} />
                      <DetailRow label="Platform Fee" value={pmt.platformFee != null ? formatDollars(pmt.platformFee) : undefined} />
                      <DetailRow label="Provider Payout" value={pmt.providerPayout != null ? formatDollars(pmt.providerPayout) : undefined} />
                    </DetailSection>
                    <DetailSection title="Card Details">
                      <DetailRow label="Method" value={pmt.paymentMethod?.replace(/_/g, ' ')} />
                      <DetailRow label="Brand" value={pmt.cardBrand} />
                      <DetailRow label="Last 4" value={pmt.cardLast4} />
                    </DetailSection>
                    {pmt.refundAmount != null && pmt.refundAmount > 0 && (
                      <DetailSection title="Refund">
                        <DetailRow label="Refund Amount" value={formatDollars(pmt.refundAmount)} />
                        <DetailRow label="Reason" value={pmt.refundReason} />
                        {pmt.refundedAt && <DetailRow label="Refunded At" value={formatDateTime(pmt.refundedAt)} />}
                      </DetailSection>
                    )}
                    {pmt.failureReason && (
                      <DetailSection title="Failure">
                        <DetailRow label="Reason" value={pmt.failureReason} />
                      </DetailSection>
                    )}
                    {pmt.booking && (
                      <DetailSection title="Booking">
                        <DetailRow label="Booking ID" value={`#${pmt.booking.id}`} />
                        <DetailRow label="Status" value={pmt.booking.status} />
                        <DetailRow label="Date" value={formatDateTime(pmt.booking.bookingDate)} />
                        {pmt.booking.customer && (
                          <DetailRow label="Customer" value={
                            <span className="inline-flex items-center gap-1.5">
                              <Avatar src={pmt.booking.customer.profileImage || undefined} name={pmt.booking.customer.name || pmt.booking.customer.email || '?'} size="sm" className="w-4 h-4 text-[8px]" />
                              {pmt.booking.customer.name || pmt.booking.customer.email}
                            </span>
                          } />
                        )}
                        {pmt.booking.provider && (
                          <DetailRow label="Provider" value={
                            <span className="inline-flex items-center gap-1.5">
                              <Avatar src={pmt.booking.provider.profileImage || undefined} name={pmt.booking.provider.name || pmt.booking.provider.email || '?'} size="sm" className="w-4 h-4 text-[8px]" />
                              {pmt.booking.provider.name || pmt.booking.provider.email}
                            </span>
                          } />
                        )}
                        {pmt.booking.service && <DetailRow label="Service" value={pmt.booking.service.serviceName} />}
                      </DetailSection>
                    )}
                    <DetailSection title="Stripe IDs">
                      <div className="col-span-full flex flex-wrap gap-1.5">
                        <StripeId id={pmt.stripePaymentId} label="PI" full />
                        <StripeId id={pmt.stripeChargeId} label="CH" full />
                        <StripeId id={pmt.paymentIntentId} label="Intent" full />
                      </div>
                    </DetailSection>
                    <DetailSection title="Timeline">
                      <DetailRow label="Created" value={formatDateTime(pmt.createdAt)} />
                      {pmt.paidAt && <DetailRow label="Paid" value={formatDateTime(pmt.paidAt)} />}
                      {pmt.refundedAt && <DetailRow label="Refunded" value={formatDateTime(pmt.refundedAt)} />}
                    </DetailSection>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Pagination page={currentPage} totalPages={totalPages} total={total} onPageChange={setPage} loading={loading} />
    </div>
  );
};

// ============================================================================
// Payouts Tab
// ============================================================================

const PayoutsTab: React.FC = () => {
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const params = useMemo<FinancePayoutsParams>(() => ({
    status: filters.status || undefined,
    type: filters.type || undefined,
    search: search || undefined,
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  }), [filters, search, page]);

  const { items, total, totalPages, loading, page: currentPage } = useFinancePayouts(params);

  const handleFilterChange = useCallback((key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  }, []);

  const filterFields: FilterField[] = [
    {
      key: 'status',
      label: 'All Statuses',
      options: [
        { label: 'Paid', value: 'PAID' },
        { label: 'Pending', value: 'PENDING' },
        { label: 'Pending Balance', value: 'PENDING_BALANCE' },
        { label: 'Submitted', value: 'SUBMITTED' },
        { label: 'In Transit', value: 'IN_TRANSIT' },
        { label: 'Failed', value: 'FAILED' },
        { label: 'Cancelled', value: 'CANCELLED' },
      ],
    },
    {
      key: 'type',
      label: 'All Types',
      options: [
        { label: 'Provider Withdrawal', value: 'PROVIDER_WITHDRAWAL' },
        { label: 'Platform Fee', value: 'PLATFORM_FEE' },
      ],
    },
  ];

  return (
    <div>
      <FilterBar
        filters={filterFields}
        values={filters}
        onChange={handleFilterChange}
        onSearch={(v) => { setSearch(v); setPage(1); }}
        searchValue={search}
        searchPlaceholder="Search payouts..."
      />

      {loading && items.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 text-primary animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground text-sm">No payouts found</div>
      ) : (
        <div className="space-y-2">
          {items.map((po) => {
            const stBadge = payoutStatusBadge(po.status);
            const isExpanded = expandedId === po.id;
            return (
              <div
                key={po.id}
                className={cn(
                  'card p-3 cursor-pointer transition-all',
                  isExpanded && 'ring-1 ring-primary/30'
                )}
                onClick={() => setExpandedId(isExpanded ? null : po.id)}
              >
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="flex-shrink-0 w-16 text-xs text-muted-foreground">
                      {new Date(po.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </div>
                    {po.provider && (
                      <div className="flex items-center gap-2 min-w-0">
                        <Avatar src={po.provider.profileImage || undefined} name={po.provider.name || po.provider.email || '?'} size="sm" />
                        <span className="text-xs text-foreground truncate">{po.provider.name || po.provider.email}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <span className="text-sm font-semibold text-foreground tabular-nums">{formatDollars(po.amount)}</span>
                      {po.platformFee > 0 && (
                        <span className="text-[10px] text-muted-foreground ml-2">
                          fee: {formatDollars(po.platformFee)} | net: {formatDollars(po.netAmount)}
                        </span>
                      )}
                    </div>
                    <Badge variant={stBadge.variant} size="sm">{po.status.replace(/_/g, ' ')}</Badge>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                  <StripeId id={po.stripeTransferId} label="TR" />
                  <StripeId id={po.stripePayoutId} label="PO" />
                  {po.failureMessage && (
                    <span className="text-[10px] text-red-400 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />{po.failureMessage}
                    </span>
                  )}
                </div>
                {isExpanded && (
                  <div className="border-t border-border mt-3 pt-3 space-y-3" onClick={(e) => e.stopPropagation()}>
                    <DetailSection title="Payout Details">
                      <DetailRow label="Type" value={po.type.replace(/_/g, ' ')} />
                      <DetailRow label="Amount" value={formatDollars(po.amount)} />
                      <DetailRow label="Platform Fee" value={formatDollars(po.platformFee)} />
                      <DetailRow label="Net Amount" value={formatDollars(po.netAmount)} />
                      <DetailRow label="Currency" value={po.currency} />
                    </DetailSection>
                    {po.provider && (
                      <DetailSection title="Provider">
                        <DetailRow label="Provider" value={
                          <span className="inline-flex items-center gap-1.5">
                            <Avatar src={po.provider.profileImage || undefined} name={po.provider.name || po.provider.email || '?'} size="sm" className="w-4 h-4 text-[8px]" />
                            {po.provider.name || po.provider.email}
                          </span>
                        } />
                        <DetailRow label="Email" value={po.provider.email} />
                        <DetailRow label="ID" value={String(po.provider.id)} />
                      </DetailSection>
                    )}
                    {po.description && (
                      <DetailSection title="Description">
                        <div className="col-span-full text-[10px] text-foreground">{po.description}</div>
                      </DetailSection>
                    )}
                    {(po.failureCode || po.failureMessage) && (
                      <DetailSection title="Failure">
                        <DetailRow label="Code" value={po.failureCode} />
                        <DetailRow label="Message" value={po.failureMessage} />
                      </DetailSection>
                    )}
                    <DetailSection title="Stripe IDs">
                      <div className="col-span-full flex flex-wrap gap-1.5">
                        <StripeId id={po.stripeTransferId} label="TR" full />
                        <StripeId id={po.stripePayoutId} label="PO" full />
                      </div>
                    </DetailSection>
                    <DetailSection title="Timeline">
                      <DetailRow label="Created" value={formatDateTime(po.createdAt)} />
                      <DetailRow label="Requested" value={formatDateTime(po.requestedAt)} />
                      {po.submittedAt && <DetailRow label="Submitted" value={formatDateTime(po.submittedAt)} />}
                      {po.paidAt && <DetailRow label="Paid" value={formatDateTime(po.paidAt)} />}
                      {po.failedAt && <DetailRow label="Failed" value={formatDateTime(po.failedAt)} />}
                    </DetailSection>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Pagination page={currentPage} totalPages={totalPages} total={total} onPageChange={setPage} loading={loading} />
    </div>
  );
};

// ============================================================================
// Refunds Tab
// ============================================================================

const RefundsTab: React.FC = () => {
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const params = useMemo<FinanceRefundsParams>(() => ({
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  }), [page]);

  const { items, total, totalPages, loading, page: currentPage } = useFinanceRefunds(params);

  return (
    <div>
      {loading && items.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 text-primary animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground text-sm">No refunds found</div>
      ) : (
        <div className="space-y-2">
          {items.map((ref) => {
            const isExpanded = expandedId === ref.id;
            return (
              <div
                key={ref.id}
                className={cn(
                  'card p-3 cursor-pointer transition-all',
                  isExpanded && 'ring-1 ring-primary/30'
                )}
                onClick={() => setExpandedId(isExpanded ? null : ref.id)}
              >
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="flex-shrink-0 w-16 text-xs text-muted-foreground">
                      {new Date(ref.refundedAt || ref.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </div>
                    <span className="text-xs text-muted-foreground">Paid: {formatDollars(ref.paymentAmount)}</span>
                    <span className="text-sm font-semibold text-red-400 tabular-nums">-{formatDollars(ref.refundAmount)}</span>
                    <Badge variant={ref.paymentAmount === ref.refundAmount ? 'error' : 'warning'} size="sm">
                      {ref.paymentAmount === ref.refundAmount ? 'Full Refund' : 'Partial'}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-primary">Booking #{ref.bookingId}</span>
                    {ref.booking.status && (
                      <Badge variant="muted" size="sm">{ref.booking.status.replace(/_/g, ' ')}</Badge>
                    )}
                    {ref.booking.cancellationType && (
                      <Badge variant={cancellationTypeBadge(ref.booking.cancellationType).variant} size="sm">
                        {ref.booking.cancellationType}
                      </Badge>
                    )}
                  </div>
                </div>
                {/* People & service */}
                <div className="flex items-center gap-4 mt-1.5 flex-wrap">
                  <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Avatar src={ref.booking.customer.profileImage || undefined} name={ref.booking.customer.name || ref.booking.customer.email || '?'} size="sm" className="w-4 h-4 text-[8px]" />
                    Customer: {ref.booking.customer.name || ref.booking.customer.email}
                  </span>
                  <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Avatar src={ref.booking.provider.profileImage || undefined} name={ref.booking.provider.name || ref.booking.provider.email || '?'} size="sm" className="w-4 h-4 text-[8px]" />
                    Provider: {ref.booking.provider.name || ref.booking.provider.email}
                  </span>
                  {ref.booking.service && (
                    <span className="text-xs text-primary">{ref.booking.service.serviceName}</span>
                  )}
                </div>
                {/* Reason */}
                {ref.refundReason && (
                  <p className="text-[10px] text-muted-foreground mt-1 truncate max-w-full">{ref.refundReason}</p>
                )}
                {isExpanded && (
                  <div className="border-t border-border mt-3 pt-3 space-y-3" onClick={(e) => e.stopPropagation()}>
                    <DetailSection title="Payment">
                      <DetailRow label="Payment Amount" value={formatDollars(ref.paymentAmount)} />
                      <DetailRow label="Payment Status" value={ref.paymentStatus} />
                      <DetailRow label="Payment Method" value={ref.paymentMethod?.replace(/_/g, ' ')} />
                    </DetailSection>
                    <DetailSection title="Refund">
                      <DetailRow label="Refund Amount" value={formatDollars(ref.refundAmount)} />
                      <DetailRow label="Reason" value={ref.refundReason} />
                    </DetailSection>
                    <DetailSection title="Booking">
                      <DetailRow label="Booking ID" value={`#${ref.bookingId}`} />
                      <DetailRow label="Status" value={ref.booking.status} />
                      {ref.booking.cancellationType && (
                        <DetailRow label="Cancellation Type" value={ref.booking.cancellationType} />
                      )}
                      {ref.booking.cancellationReason && (
                        <DetailRow label="Cancellation Reason" value={ref.booking.cancellationReason} />
                      )}
                      {ref.booking.cancelledBy != null && (
                        <DetailRow label="Cancelled By" value={ref.booking.cancelledByName || String(ref.booking.cancelledBy)} />
                      )}
                    </DetailSection>
                    <DetailSection title="People">
                      <DetailRow label="Customer" value={
                        <span className="inline-flex items-center gap-1.5">
                          <Avatar src={ref.booking.customer.profileImage || undefined} name={ref.booking.customer.name || ref.booking.customer.email || '?'} size="sm" className="w-4 h-4 text-[8px]" />
                          {ref.booking.customer.name || ref.booking.customer.email}
                        </span>
                      } />
                      <DetailRow label="Provider" value={
                        <span className="inline-flex items-center gap-1.5">
                          <Avatar src={ref.booking.provider.profileImage || undefined} name={ref.booking.provider.name || ref.booking.provider.email || '?'} size="sm" className="w-4 h-4 text-[8px]" />
                          {ref.booking.provider.name || ref.booking.provider.email}
                        </span>
                      } />
                      {ref.booking.service && <DetailRow label="Service" value={ref.booking.service.serviceName} />}
                    </DetailSection>
                    <DetailSection title="Stripe IDs">
                      <div className="col-span-full flex flex-wrap gap-1.5">
                        <StripeId id={ref.stripePaymentId} label="PI" full />
                        <StripeId id={ref.stripeChargeId} label="CH" full />
                        <StripeId id={ref.paymentIntentId} label="Intent" full />
                      </div>
                    </DetailSection>
                    <DetailSection title="Timeline">
                      <DetailRow label="Created" value={formatDateTime(ref.createdAt)} />
                      {ref.paidAt && <DetailRow label="Paid" value={formatDateTime(ref.paidAt)} />}
                      {ref.refundedAt && <DetailRow label="Refunded" value={formatDateTime(ref.refundedAt)} />}
                    </DetailSection>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Pagination page={currentPage} totalPages={totalPages} total={total} onPageChange={setPage} loading={loading} />
    </div>
  );
};

// ============================================================================
// Cancellations Tab
// ============================================================================

const CancellationsTab: React.FC = () => {
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const params = useMemo<FinanceCancellationsParams>(() => ({
    cancellationType: filters.cancellationType || undefined,
    status: filters.status || undefined,
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  }), [filters, page]);

  const { items, total, totalPages, loading, summary, page: currentPage } = useFinanceCancellations(params);

  const handleFilterChange = useCallback((key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  }, []);

  const filterFields: FilterField[] = [
    {
      key: 'cancellationType',
      label: 'All Types',
      options: [
        { label: 'Customer', value: 'CUSTOMER' },
        { label: 'Provider', value: 'PROVIDER' },
        { label: 'System', value: 'SYSTEM' },
        { label: 'Auto Expired', value: 'AUTO_EXPIRED' },
        { label: 'Conflict', value: 'CONFLICT' },
      ],
    },
    {
      key: 'status',
      label: 'All Statuses',
      options: [
        { label: 'Cancelled', value: 'CANCELLED' },
        { label: 'Cancelling', value: 'CANCELLING' },
      ],
    },
  ];

  return (
    <div>
      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <StatCard
            label="Total Cancellations"
            value={formatNumber(summary.total)}
            icon={XCircle}
            color="text-red-400"
            bgColor="bg-red-400/10"
          />
          <StatCard
            label="Total Refunded"
            value={formatDollarsCompact(summary.totalRefunded)}
            icon={RotateCcw}
            color="text-orange-400"
            bgColor="bg-orange-400/10"
          />
          <StatCard
            label="Total Penalties"
            value={formatDollarsCompact(summary.totalPenalties)}
            icon={AlertTriangle}
            color="text-yellow-400"
            bgColor="bg-yellow-400/10"
          />
          <div className="card p-4">
            <p className="text-xs text-muted-foreground mb-1">By Type</p>
            <div className="space-y-1">
              {Object.entries(summary.byType).map(([type, count]) => (
                <div key={type} className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">{type}</span>
                  <span className="text-foreground font-medium">{count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <FilterBar
        filters={filterFields}
        values={filters}
        onChange={handleFilterChange}
      />

      {loading && items.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 text-primary animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground text-sm">No cancellations found</div>
      ) : (
        <div className="space-y-2">
          {items.map((c) => {
            const isExpanded = expandedId === c.id;
            return (
              <div
                key={c.id}
                className={cn(
                  'card p-3 cursor-pointer transition-all',
                  isExpanded && 'ring-1 ring-primary/30'
                )}
                onClick={() => setExpandedId(isExpanded ? null : c.id)}
              >
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="flex-shrink-0 w-16 text-xs text-muted-foreground">
                      {new Date(c.cancelledAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </div>
                    <span className="text-xs text-primary">Booking #{c.bookingId}</span>
                    <Badge variant={cancellationTypeBadge(c.cancellationType).variant} size="sm">
                      {c.cancellationType}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    {c.cancellationReason && (
                      <span className="text-xs text-muted-foreground truncate max-w-[200px]">{c.cancellationReason}</span>
                    )}
                    {c.customer && (
                      <span className="text-xs text-muted-foreground">{c.customer.name || c.customer.email}</span>
                    )}
                  </div>
                </div>
                {/* Financial impact indicators */}
                <div className="flex items-center gap-3 mt-1.5">
                  <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
                    {c.financialImpact.hasPayment ? (
                      <CheckCircle className="h-3 w-3 text-emerald-400" />
                    ) : (
                      <X className="h-3 w-3 text-muted-foreground/40" />
                    )}
                    Payment {c.financialImpact.paymentAmount != null && `(${formatDollars(c.financialImpact.paymentAmount)})`}
                  </span>
                  <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
                    {c.financialImpact.hasRefund ? (
                      <CheckCircle className="h-3 w-3 text-orange-400" />
                    ) : (
                      <X className="h-3 w-3 text-muted-foreground/40" />
                    )}
                    Refund {c.financialImpact.refundAmount != null && `(${formatDollars(c.financialImpact.refundAmount)})`}
                  </span>
                  <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
                    {c.financialImpact.hasPenalty ? (
                      <CheckCircle className="h-3 w-3 text-red-400" />
                    ) : (
                      <X className="h-3 w-3 text-muted-foreground/40" />
                    )}
                    Penalty {c.financialImpact.penaltyAmount != null && `(${formatDollars(c.financialImpact.penaltyAmount)})`}
                  </span>
                </div>
                {isExpanded && (
                  <div className="border-t border-border mt-3 pt-3 space-y-3" onClick={(e) => e.stopPropagation()}>
                    <DetailSection title="Booking">
                      <DetailRow label="Booking ID" value={`#${c.bookingId}`} />
                      <DetailRow label="Booking Date" value={formatDateTime(c.bookingDate)} />
                      <DetailRow label="Status" value={c.status} />
                    </DetailSection>
                    <DetailSection title="Cancellation">
                      <DetailRow label="Type" value={c.cancellationType} />
                      <DetailRow label="Reason" value={c.cancellationReason} />
                      {c.cancelledBy != null && (
                        <DetailRow label="Cancelled By" value={String(c.cancelledBy)} />
                      )}
                      <DetailRow label="Cancelled At" value={formatDateTime(c.cancelledAt)} />
                    </DetailSection>
                    <DetailSection title="People">
                      {c.customer && (
                        <DetailRow label="Customer" value={`${c.customer.name || '—'} (${c.customer.email || '—'})`} />
                      )}
                      {c.provider && (
                        <DetailRow label="Provider" value={`${c.provider.name || '—'} (${c.provider.email || '—'})`} />
                      )}
                      {c.service && <DetailRow label="Service" value={c.service.serviceName} />}
                    </DetailSection>
                    <DetailSection title="Financial Impact">
                      <DetailRow label="Has Payment" value={c.financialImpact.hasPayment ? 'Yes' : 'No'} />
                      {c.financialImpact.paymentAmount != null && (
                        <DetailRow label="Payment Amount" value={formatDollars(c.financialImpact.paymentAmount)} />
                      )}
                      <DetailRow label="Has Refund" value={c.financialImpact.hasRefund ? 'Yes' : 'No'} />
                      {c.financialImpact.refundAmount != null && (
                        <DetailRow label="Refund Amount" value={formatDollars(c.financialImpact.refundAmount)} />
                      )}
                      <DetailRow label="Has Penalty" value={c.financialImpact.hasPenalty ? 'Yes' : 'No'} />
                      {c.financialImpact.penaltyAmount != null && (
                        <DetailRow label="Penalty Amount" value={formatDollars(c.financialImpact.penaltyAmount)} />
                      )}
                    </DetailSection>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Pagination page={currentPage} totalPages={totalPages} total={total} onPageChange={setPage} loading={loading} />
    </div>
  );
};

// ============================================================================
// Operations Tab
// ============================================================================

const OperationsTab: React.FC = () => {
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const params = useMemo<FinanceOperationsParams>(() => ({
    operationType: filters.operationType || undefined,
    status: filters.status || undefined,
    search: search || undefined,
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  }), [filters, search, page]);

  const { items, total, totalPages, loading, page: currentPage } = useFinanceOperations(params);

  const handleFilterChange = useCallback((key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  }, []);

  const filterFields: FilterField[] = [
    {
      key: 'operationType',
      label: 'All Types',
      options: [
        { label: 'Payment Intent', value: 'PAYMENT_INTENT_CREATE' },
        { label: 'Payment Capture', value: 'PAYMENT_CAPTURE' },
        { label: 'Refund', value: 'REFUND' },
        { label: 'Transfer', value: 'TRANSFER' },
        { label: 'Payout', value: 'PAYOUT' },
        { label: 'Reconciliation', value: 'RECONCILIATION' },
      ],
    },
    {
      key: 'status',
      label: 'All Statuses',
      options: [
        { label: 'Completed', value: 'COMPLETED' },
        { label: 'Pending', value: 'PENDING' },
        { label: 'Processing', value: 'PROCESSING' },
        { label: 'Failed', value: 'FAILED' },
        { label: 'Retrying', value: 'RETRYING' },
      ],
    },
  ];

  return (
    <div>
      <FilterBar
        filters={filterFields}
        values={filters}
        onChange={handleFilterChange}
        onSearch={(v) => { setSearch(v); setPage(1); }}
        searchValue={search}
        searchPlaceholder="Search operations..."
      />

      {loading && items.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 text-primary animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground text-sm">No operations found</div>
      ) : (
        <div className="space-y-2">
          {items.map((op) => {
            const stBadge = operationStatusBadge(op.status);
            const highAttempts = op.attemptCount > 3;
            const isExpanded = expandedId === op.id;
            return (
              <div
                key={op.id}
                className={cn(
                  'card p-3 cursor-pointer transition-all',
                  isExpanded && 'ring-1 ring-primary/30'
                )}
                onClick={() => setExpandedId(isExpanded ? null : op.id)}
              >
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="flex-shrink-0 w-16 text-xs text-muted-foreground">
                      {new Date(op.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </div>
                    <Badge variant="info" size="sm">{op.operationType.replace(/_/g, ' ')}</Badge>
                    <Badge variant={stBadge.variant} size="sm">{op.status}</Badge>
                    <span className={cn('text-xs tabular-nums', highAttempts ? 'text-red-400 font-semibold' : 'text-muted-foreground')}>
                      {op.attemptCount} attempt{op.attemptCount !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                  {op.errorMessage && (
                    <span className="text-[10px] text-red-400 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />{op.errorMessage}
                    </span>
                  )}
                  <StripeId id={op.stripePaymentIntentId} label="PI" />
                  <StripeId id={op.stripeChargeId} label="CH" />
                  <StripeId id={op.stripeTransferId} label="TR" />
                  <StripeId id={op.stripeRefundId} label="RE" />
                  <StripeId id={op.stripePayoutId} label="PO" />
                  <span className="text-[10px] font-mono text-muted-foreground/50 truncate max-w-[180px]" title={op.idempotencyKey}>
                    key: {op.idempotencyKey.slice(0, 12)}...
                  </span>
                </div>
                {isExpanded && (
                  <div className="border-t border-border mt-3 pt-3 space-y-3" onClick={(e) => e.stopPropagation()}>
                    <DetailSection title="Operation">
                      <DetailRow label="Type" value={op.operationType.replace(/_/g, ' ')} />
                      <DetailRow label="Status" value={op.status} />
                      {op.paymentId != null && <DetailRow label="Payment ID" value={String(op.paymentId)} />}
                    </DetailSection>
                    <DetailSection title="Attempts">
                      <DetailRow label="Count" value={String(op.attemptCount)} />
                      <DetailRow label="First Attempt" value={formatDateTime(op.firstAttemptAt)} />
                      <DetailRow label="Last Attempt" value={formatDateTime(op.lastAttemptAt)} />
                    </DetailSection>
                    {op.errorMessage && (
                      <DetailSection title="Error">
                        <div className="col-span-full text-[10px] text-red-400">{op.errorMessage}</div>
                      </DetailSection>
                    )}
                    {(op.reconciledAt || op.reconciledBy) && (
                      <DetailSection title="Reconciliation">
                        {op.reconciledAt && <DetailRow label="Reconciled At" value={formatDateTime(op.reconciledAt)} />}
                        {op.reconciledBy && <DetailRow label="Reconciled By" value={op.reconciledBy} />}
                      </DetailSection>
                    )}
                    <DetailSection title="Stripe IDs">
                      <div className="col-span-full flex flex-wrap gap-1.5">
                        <StripeId id={op.stripePaymentIntentId} label="PI" full />
                        <StripeId id={op.stripeChargeId} label="CH" full />
                        <StripeId id={op.stripeTransferId} label="TR" full />
                        <StripeId id={op.stripeRefundId} label="RE" full />
                        <StripeId id={op.stripePayoutId} label="PO" full />
                      </div>
                    </DetailSection>
                    <DetailSection title="Idempotency Key">
                      <div className="col-span-full">
                        <span
                          className="inline-flex items-center gap-1 text-[10px] font-mono text-muted-foreground bg-muted/20 px-1.5 py-0.5 rounded cursor-pointer hover:text-foreground transition-colors"
                          title={op.idempotencyKey}
                          onClick={() => navigator.clipboard.writeText(op.idempotencyKey)}
                        >
                          {op.idempotencyKey}
                          <Copy className="h-2.5 w-2.5" />
                        </span>
                      </div>
                    </DetailSection>
                    <DetailSection title="Timeline">
                      <DetailRow label="Created" value={formatDateTime(op.createdAt)} />
                      <DetailRow label="Updated" value={formatDateTime(op.updatedAt)} />
                      {op.completedAt && <DetailRow label="Completed" value={formatDateTime(op.completedAt)} />}
                    </DetailSection>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Pagination page={currentPage} totalPages={totalPages} total={total} onPageChange={setPage} loading={loading} />
    </div>
  );
};

// ============================================================================
// Main Finance Page
// ============================================================================

const Finance: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabKey>('overview');

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Tab bar */}
      <div className="flex-shrink-0 border-b border-border bg-card">
        <div className="flex items-center gap-1 px-4 py-2 overflow-x-auto scrollbar-thin">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={cn(
                  'flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-all whitespace-nowrap',
                  isActive
                    ? 'bg-primary/15 text-primary font-medium border border-primary/30'
                    : 'text-muted-foreground hover:bg-primary/10 hover:text-foreground'
                )}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto p-4 lg:p-6">
        {activeTab === 'overview' && <OverviewTab />}
        {activeTab === 'ledger' && <LedgerTab />}
        {activeTab === 'payments' && <PaymentsTab />}
        {activeTab === 'payouts' && <PayoutsTab />}
        {activeTab === 'refunds' && <RefundsTab />}
        {activeTab === 'cancellations' && <CancellationsTab />}
        {activeTab === 'operations' && <OperationsTab />}
      </div>
    </div>
  );
};

export default Finance;
